import React from 'react';
import type { CSSProperties } from 'react';
import {
  add_reason,
  edit_reason_label,
  move_reason,
  remove_reason,
  validate_gen_globals
} from './gen_config';
import type { GenGlobals, GlobalsBounds, GlobalsKey } from './gen_config';

interface Props {
  globals: GenGlobals;
  bounds: GlobalsBounds;
  disabled: boolean;
  on_change: <K extends GlobalsKey>(key: K, value: GenGlobals[K]) => void;
}

const INPUT: CSSProperties = {
  padding: '2px 6px',
  borderRadius: 6,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  margin: 0
};

const SMALL_BUTTON: CSSProperties = {
  margin: 0,
  padding: '0 8px',
  borderRadius: 6,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  fontSize: 12,
  lineHeight: '22px'
};

const PRIORITY_KEYS = ['P0', 'P1', 'P2'] as const;

const ERROR_TEXT: CSSProperties = { color: 'var(--state-error-text)', margin: 0 };

// Team-wide sales task settings stored beside the rules.
function GlobalSettingsCard({ globals: g, bounds, disabled, on_change }: Props) {
  const reasons = g.skip_reasons;
  const errors = validate_gen_globals(g);
  return (
    <div
      data-testid="gen-global-settings"
      className="rounded-md border border-line px-4 py-3 flex flex-col gap-3"
      style={{ opacity: disabled ? 0.7 : 1 }}
    >
      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        Global settings
      </div>

      <div className="flex flex-col gap-1">
        <div className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
          Skip reasons
        </div>
        <ol className="flex flex-col gap-1" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {reasons.map((r, i) => (
            <li key={r.id ?? r.code} className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                aria-label={`Skip reason ${i + 1}`}
                value={r.label}
                disabled={disabled}
                maxLength={64}
                aria-invalid={
                  errors.duplicate_reasons.includes(i) ||
                  errors.empty_reasons.includes(i) ||
                  undefined
                }
                onChange={(e) =>
                  on_change('skip_reasons', edit_reason_label(reasons, i, e.target.value))
                }
                style={{ ...INPUT, width: 220 }}
              />
              <code className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {r.code || '—'}
              </code>
              {errors.empty_reasons.includes(i) ? (
                <span className="text-xs" style={ERROR_TEXT}>
                  Enter a label, or remove this reason
                </span>
              ) : null}
              {errors.duplicate_reasons.includes(i) ? (
                <span className="text-xs" style={ERROR_TEXT}>
                  Same code as another reason
                </span>
              ) : null}
              <button
                type="button"
                aria-label={`Move ${r.label || 'reason'} up`}
                disabled={disabled || i === 0}
                onClick={() => on_change('skip_reasons', move_reason(reasons, i, -1))}
                style={SMALL_BUTTON}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${r.label || 'reason'} down`}
                disabled={disabled || i === reasons.length - 1}
                onClick={() => on_change('skip_reasons', move_reason(reasons, i, 1))}
                style={SMALL_BUTTON}
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={`Remove ${r.label || 'reason'}`}
                disabled={disabled || reasons.length <= 1}
                title={reasons.length <= 1 ? 'Keep at least one reason' : undefined}
                onClick={() => on_change('skip_reasons', remove_reason(reasons, i))}
                style={SMALL_BUTTON}
              >
                Remove
              </button>
            </li>
          ))}
        </ol>
        <div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => on_change('skip_reasons', add_reason(reasons))}
            style={SMALL_BUTTON}
          >
            Add reason
          </button>
        </div>
      </div>

      <div
        className="flex items-center gap-4 flex-wrap text-xs"
        style={{ color: 'var(--text-muted)' }}
      >
        <span className="font-medium">Unclaimed alert after (minutes)</span>
        {PRIORITY_KEYS.map((p) => (
          <label key={p} htmlFor={`gen-buffer-${p}`} className="inline-flex items-center gap-1.5">
            {p}
            <input
              id={`gen-buffer-${p}`}
              type="number"
              aria-label={`Unclaimed alert buffer ${p} (minutes)`}
              min={bounds.min_unclaimed_buffer_minutes}
              max={bounds.max_unclaimed_buffer_minutes}
              value={g.unclaimed_alert_buffer_minutes[p] ?? ''}
              disabled={disabled}
              onChange={(e) =>
                on_change('unclaimed_alert_buffer_minutes', {
                  ...g.unclaimed_alert_buffer_minutes,
                  [p]: e.target.value
                })
              }
              style={{ ...INPUT, width: 72 }}
            />
          </label>
        ))}
      </div>

      <div
        className="flex items-center gap-6 flex-wrap text-xs"
        style={{ color: 'var(--text-muted)' }}
      >
        <label htmlFor="gen-claim-idle" className="inline-flex items-center gap-1.5 font-medium">
          Claim idle (minutes)
          <input
            id="gen-claim-idle"
            type="number"
            min={bounds.min_claim_idle_minutes}
            max={bounds.max_claim_idle_minutes}
            value={g.claim_idle_minutes ?? ''}
            disabled={disabled}
            onChange={(e) => on_change('claim_idle_minutes', e.target.value)}
            style={{ ...INPUT, width: 72 }}
          />
        </label>
        <label
          htmlFor="gen-call-start"
          className="inline-flex items-center gap-1.5 font-medium"
          title="The student's local calling hours. Tasks are still created at any hour; outside these hours a task is grayed out and calling or messaging asks to confirm."
        >
          Call window from
          <input
            id="gen-call-start"
            type="time"
            value={g.call_window_start}
            disabled={disabled}
            onChange={(e) => on_change('call_window_start', e.target.value)}
            style={INPUT}
          />
        </label>
        <label htmlFor="gen-call-end" className="inline-flex items-center gap-1.5 font-medium">
          to
          <input
            id="gen-call-end"
            type="time"
            aria-label="Call window to"
            value={g.call_window_end}
            disabled={disabled}
            onChange={(e) => on_change('call_window_end', e.target.value)}
            style={INPUT}
          />
        </label>
        <label
          htmlFor="gen-require-outcome"
          className="inline-flex items-center gap-1.5 font-medium"
        >
          <input
            id="gen-require-outcome"
            type="checkbox"
            checked={g.require_outcome}
            disabled={disabled}
            onChange={(e) => on_change('require_outcome', e.target.checked)}
          />
          Require an outcome to close a task
        </label>
      </div>
      {errors.call_window ? (
        <p role="alert" className="text-xs" style={ERROR_TEXT}>
          {errors.call_window}
        </p>
      ) : null}

      <div className="flex flex-col gap-0.5 text-xs">
        <label
          htmlFor="gen-duplicate-account-skip"
          className="inline-flex items-center gap-1.5 font-medium"
        >
          <input
            id="gen-duplicate-account-skip"
            type="checkbox"
            checked={g.duplicate_account_skip}
            disabled={disabled}
            aria-describedby="gen-duplicate-account-skip-help"
            onChange={(e) => on_change('duplicate_account_skip', e.target.checked)}
          />
          Skip duplicate accounts
        </label>
        <span id="gen-duplicate-account-skip-help" style={{ color: 'var(--text-muted)' }}>
          Skip a task when an older account with the same phone number is already a member or has
          attended a session
        </span>
      </div>

      <label
        htmlFor="gen-alert-mentions"
        className="flex flex-col gap-1 text-xs font-medium"
        style={{ color: 'var(--text-muted)' }}
      >
        Unclaimed alert mentions (Slack user IDs, comma separated)
        <input
          id="gen-alert-mentions"
          type="text"
          value={g.unclaimed_alert_mentions}
          disabled={disabled}
          onChange={(e) => on_change('unclaimed_alert_mentions', e.target.value)}
          aria-invalid={errors.bad_mentions.length > 0 || undefined}
          style={{ ...INPUT, maxWidth: 480 }}
        />
        {errors.bad_mentions.length ? (
          <span role="alert" style={ERROR_TEXT}>
            Not a Slack user ID (e.g. U012AB3CD): {errors.bad_mentions.join(', ')}
          </span>
        ) : null}
      </label>

      <label
        htmlFor="gen-alert-channel"
        className="flex flex-col gap-1 text-xs font-medium"
        style={{ color: 'var(--text-muted)' }}
      >
        Unclaimed alert channel (Slack channel ID; empty = alerts off)
        <input
          id="gen-alert-channel"
          type="text"
          value={g.unclaimed_alert_channel_id}
          disabled={disabled}
          maxLength={32}
          placeholder="C012AB3CD"
          onChange={(e) => on_change('unclaimed_alert_channel_id', e.target.value)}
          aria-invalid={!!errors.alert_channel || undefined}
          style={{ ...INPUT, maxWidth: 240 }}
        />
        {errors.alert_channel ? (
          <span role="alert" style={ERROR_TEXT}>
            {errors.alert_channel}
          </span>
        ) : null}
      </label>
    </div>
  );
}

export default GlobalSettingsCard;
